import PropTypes from 'prop-types';

import { IconCheckmark, Spacer, Table } from '@zillow/constellation';
import { Paragraph, Text, Tooltip } from '@zillow/constellation-10';

import {
  StyledCenteredTableCell,
  StyledHighlightedTableCell,
  StyledHighlightedTableHeaderCell,
  StyledTable,
} from './StyledComponents';

const propTypes = {
  onOpenLostRentTooltip: PropTypes.func.isRequired,
  onOpenUnitDamageTooltip: PropTypes.func.isRequired,
  marginBottom: PropTypes.number,
  title: PropTypes.element,
};

const defaultProps = {
  marginBottom: 9,
};

const RentalProtectionCoverageTableSection = ({
  onOpenLostRentTooltip,
  onOpenUnitDamageTooltip,
  title,
  marginBottom,
}) => {
  return (
    <div data-testid="RentalProtectionCoverageTableSection">
      {title ? (
        title
      ) : (
        <Paragraph
          css={{
            textStyle: 'heading-sm',
            marginBottom: '200',
            fontFamily: 'heading-alt',
            color: 'text.express.insight.strong',
          }}
        >
          Complete your rental coverage
        </Paragraph>
      )}
      <Paragraph css={{ marginBottom: '400' }}>
        Rental Protection picks up where your landlord property insurance leaves off.
      </Paragraph>
      <Paragraph>Here&apos;s how your policies could work together:</Paragraph>
      <Spacer marginTop="md" marginBottom={marginBottom}>
        <StyledTable
          appearance="horizontal"
          aria-label="Medium-sized horizontal table"
          size="md"
          alignments={['left', 'center', 'center']}
          marginTop="md"
          marginBottom="xl"
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell></Table.HeaderCell>
              <StyledHighlightedTableHeaderCell>
                <Text css={{ textStyle: { base: 'body-xs-bold', lg: 'body-lg-bold' } }}>Rental Protection</Text>
              </StyledHighlightedTableHeaderCell>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-xs-bold', lg: 'body-lg-bold' } }}>Landlord insurance*</Text>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Lost rent if a renter can&apos;t make a payment
                </Text>
              </Table.HeaderCell>
              <StyledHighlightedTableCell>
                <IconCheckmark size="sm" fontColor="brand" />
              </StyledHighlightedTableCell>
              <StyledCenteredTableCell></StyledCenteredTableCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>Pet damage to unit</Text>
              </Table.HeaderCell>
              <StyledHighlightedTableCell>
                <IconCheckmark size="sm" fontColor="brand" />
              </StyledHighlightedTableCell>
              <StyledCenteredTableCell></StyledCenteredTableCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>Renter damage to unit</Text>
              </Table.HeaderCell>
              <StyledHighlightedTableCell>
                <IconCheckmark size="sm" fontColor="brand" />
              </StyledHighlightedTableCell>
              <StyledCenteredTableCell></StyledCenteredTableCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Costs and legal fees associated with eviction
                </Text>
              </Table.HeaderCell>
              <StyledHighlightedTableCell>
                <IconCheckmark size="sm" fontColor="brand" />
              </StyledHighlightedTableCell>
              <StyledCenteredTableCell></StyledCenteredTableCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Lost rent due to other causes{' '}
                  <Tooltip
                    content="Dwelling insurance typically covers lost rent if your property becomes uninhabitable due to lightning, wind, fire, smoke, or other covered perils."
                    onOpenChange={(open) => open && onOpenLostRentTooltip()}
                  />
                </Text>
              </Table.HeaderCell>
              <StyledHighlightedTableCell></StyledHighlightedTableCell>
              <StyledCenteredTableCell>
                <IconCheckmark size="sm" />
              </StyledCenteredTableCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Damage to unit by other causes{' '}
                  <Tooltip
                    content="Dwelling insurance typically covers repairs for sudden, accidental damage to your property due to lightning, wind, fire, smoke, or other covered perils."
                    onOpenChange={(open) => open && onOpenUnitDamageTooltip()}
                  />
                </Text>
              </Table.HeaderCell>
              <StyledHighlightedTableCell></StyledHighlightedTableCell>
              <StyledCenteredTableCell>
                <IconCheckmark size="sm" />
              </StyledCenteredTableCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Personal liability coverage for landlord
                </Text>
              </Table.HeaderCell>
              <StyledHighlightedTableCell></StyledHighlightedTableCell>
              <StyledCenteredTableCell>
                <IconCheckmark size="sm" />
              </StyledCenteredTableCell>
            </Table.Row>
            <Table.Row>
              <Table.HeaderCell>
                <Text css={{ textStyle: { base: 'body-sm', lg: 'body-bold' } }}>
                  Damage to landlord personal property
                </Text>
              </Table.HeaderCell>
              <StyledHighlightedTableCell></StyledHighlightedTableCell>
              <StyledCenteredTableCell>
                <IconCheckmark size="sm" />
              </StyledCenteredTableCell>
            </Table.Row>
          </Table.Body>
        </StyledTable>
      </Spacer>
    </div>
  );
};

RentalProtectionCoverageTableSection.propTypes = propTypes;
RentalProtectionCoverageTableSection.defaultProps = defaultProps;

export default RentalProtectionCoverageTableSection;
